import React, { useEffect } from 'react';
import './App.css';
import { FluidDropdown } from './components/ui/fluid-dropdown';
import { MobileMenu } from './components/ui/MobileMenu';

interface Feature {
  id: string;
  title: string;
  description: string;
  icon: string;
}

interface Category {
  id: string;
  name: string;
  description: string;
  color: string;
  icon: string;
  features: Feature[];
}

interface CategoryPageProps {
  category: Category;
  onBack: () => void;
  categories: Category[];
  onCategorySelect: (categoryId: string) => void;
  currentCategory: string | null;
}

function CategoryPage({ category, onBack, categories, onCategorySelect, currentCategory }: CategoryPageProps) {
  // Scroll to top when category page opens
  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'auto' });
  }, [category.id]);

  return (
    <div className="app">
      {/* Navigation */}
      <nav className="nav nav-scrolled">
        <div className="nav-container">
          <div className="logo" onClick={onBack} style={{ cursor: 'pointer' }}>
            <img src="/icon.png" alt="Systemise Life" className="logo-icon" />
          </div>
          <div className="nav-center">
            <FluidDropdown categories={categories} onCategorySelect={onCategorySelect} currentCategory={currentCategory} />
          </div>
          <div className="nav-links">
            <a href="#download" className="nav-cta">Download Now</a>
          </div>
          {/* Mobile Menu */}
          <MobileMenu>
            <a href="#download" className="nav-cta">Download Now</a>
          </MobileMenu>
        </div>
      </nav>

      {/* Category Hero */}
      <section className="category-hero" style={{ '--category-color': category.color } as React.CSSProperties}>
        <div className="category-hero-background">
          <div className="category-gradient-orb" style={{ background: category.color }}></div>
        </div>
        <div className="container">
          <button onClick={() => {
            onBack();
            // Scroll to categories section after navigation
            setTimeout(() => {
              const categoriesSection = document.getElementById('categories');
              if (categoriesSection) {
                categoriesSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
              }
            }, 100);
          }} className="back-button">
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
              <path d="M12.5 15L7.5 10L12.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
            Back to Categories
          </button>
          <div className="category-hero-content">
            <div className="category-hero-icon" style={{ background: category.color }}>
              {category.icon}
            </div>
            <h1 className="category-hero-title">{category.name}</h1>
            <p className="category-hero-description">{category.description}</p>
          </div>
        </div>
      </section>

      {/* Features Grid */}
      <section className="category-features-section">
        <div className="container">
          <div className="features-header">
            <h2 className="features-title">All Features</h2>
            <p className="features-subtitle">{category.features.length} powerful tools to help you organize</p>
          </div>
          <div className="features-grid-detailed">
            {category.features.map((feature, index) => (
              <div
                key={feature.id}
                className="feature-card-detailed"
                style={{ 
                  '--category-color': category.color,
                  animationDelay: `${index * 50}ms`
                } as React.CSSProperties}
              >
                <div className="feature-icon-detailed">{feature.icon}</div>
                <h3 className="feature-title-detailed">{feature.title}</h3>
                <p className="feature-description-detailed">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="category-cta">
        <div className="container">
          <div className="category-cta-content">
            <h2 className="category-cta-title">Ready to Get Started?</h2>
            <p className="category-cta-subtitle">
              Download Systemise Life and start organizing your {category.name.toLowerCase()} today
            </p>
            <a href="#download" className="btn btn-primary btn-large">
              Download Free
              <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                <path d="M7.5 15L12.5 10L7.5 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="footer">
        <div className="container">
          <div className="footer-content">
            <div className="footer-brand">
              <div className="logo">
                <img src="/icon.png" alt="Systemise Life" className="logo-icon" />
              </div>
              <p className="footer-tagline">Organize your entire life in one place</p>
            </div>
            <div className="footer-links">
              <div className="footer-column">
                <h4>Product</h4>
                <a href="#" onClick={(e) => { e.preventDefault(); onBack(); }}>Categories</a>
                <a href="#pricing">Pricing</a>
                <a href="#download">Download</a>
              </div>
              <div className="footer-column">
                <h4>Company</h4>
                <a href="#">About</a>
                <a href="#">Blog</a>
                <a href="#">Contact</a>
              </div>
              <div className="footer-column">
                <h4>Support</h4>
                <a href="#">Help Center</a>
                <a href="#">Privacy Policy</a>
                <a href="#">Terms of Service</a>
              </div>
            </div>
          </div>
          <div className="footer-bottom">
            <p>© 2024 Systemise Life. All rights reserved.</p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default CategoryPage;


